import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useInEntityPanel } from '../components/page/entity-panel-scope.js';

/**
 * The active tab, kept in the URL as `?tab=<id>` so a reload, a shared link and browser back all land on the same
 * tab. An unknown or missing value falls back to `fallback`. Switching tabs replaces the history entry (tabs are
 * not pages) and keeps every other query parameter.
 *
 * Inside a side panel (`EntityPanelScope`) the tab is the panel's own state: the URL belongs to the page around it,
 * whose own tabs would otherwise switch with the panel's.
 */
export function useTabParam<T extends string>(
  ids: readonly T[],
  fallback: T,
  param = 'tab',
): [T, (id: string) => void] {
  const inPanel = useInEntityPanel();
  const [searchParams, setSearchParams] = useSearchParams();
  const [panelTab, setPanelTab] = useState<string | null>(null);
  const requested = inPanel ? panelTab : searchParams.get(param);
  const active = ids.find((id) => id === requested) ?? fallback;

  const setActive = (id: string) => {
    if (inPanel) {
      setPanelTab(id);
      return;
    }
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (id === fallback) next.delete(param);
        else next.set(param, id);
        return next;
      },
      { replace: true },
    );
  };

  return [active, setActive];
}
